import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/Button'
import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import {
  createPolePerformance,
  extractApiErrorMessage,
  fetchArtistsOptions,
  updatePolePerformance,
} from '@/services/events'
import {
  ArtistOptionProps,
  EventPerformanceProps,
  PerformanceMutationPayloadProps,
} from '@/types/eventsProps'

interface FormEventPerformanceProps {
  eventId: string
  poleId: string
  performance?: EventPerformanceProps
  onSaved: () => Promise<void> | void
}

interface PerformanceFormValues {
  artistId: string
  title: string
  performanceDate: string
  startTime: string
  endTime: string
  sortOrder: string
  isHeadliner: boolean
  note: string
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  const timezoneOffset = parsedDate.getTimezoneOffset()
  const localDate = new Date(parsedDate.getTime() - timezoneOffset * 60000)
  return localDate.toISOString().slice(0, 16)
}

function toIsoDateTime(value: string) {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  return parsedDate.toISOString()
}

function toOptionalNumber(value: string) {
  if (!value.trim()) return undefined

  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : undefined
}

export function FormEventPerformance({
  eventId,
  poleId,
  performance,
  onSaved,
}: FormEventPerformanceProps) {
  const { closeModal } = useModal()
  const { showToast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingArtists, setIsLoadingArtists] = useState(false)
  const [artists, setArtists] = useState<ArtistOptionProps[]>([])
  const [artistQuery, setArtistQuery] = useState('')
  const [selectedArtist, setSelectedArtist] =
    useState<ArtistOptionProps | null>(null)
  const [isArtistDropdownOpen, setIsArtistDropdownOpen] = useState(false)
  const searchRequestIdRef = useRef(0)

  const { register, handleSubmit, setValue, watch } =
    useForm<PerformanceFormValues>({
      defaultValues: {
        artistId: '',
        title: '',
        performanceDate: '',
        startTime: '',
        endTime: '',
        sortOrder: '',
        isHeadliner: false,
        note: '',
      },
    })

  const selectedArtistId = watch('artistId')
  const normalizedArtistQuery = artistQuery.trim()

  const artistOptions = useMemo(() => {
    if (!normalizedArtistQuery) {
      return artists
    }

    const normalizedQuery = normalizedArtistQuery.toLowerCase()
    return artists.filter((artist) =>
      artist.title.toLowerCase().includes(normalizedQuery),
    )
  }, [artists, normalizedArtistQuery])

  const onSubmit = async (data: PerformanceFormValues) => {
    const performanceDate = toIsoDateTime(data.performanceDate)

    if (!data.artistId || !performanceDate) {
      showToast('Artista e data da performance sao obrigatorios.', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    const payload: PerformanceMutationPayloadProps = {
      artistId: data.artistId,
      title: data.title,
      performanceDate,
      startTime: data.startTime,
      endTime: data.endTime,
      sortOrder: toOptionalNumber(data.sortOrder),
      isHeadliner: data.isHeadliner,
      note: data.note,
    }

    try {
      setIsLoading(true)

      if (performance?.id) {
        await updatePolePerformance(eventId, poleId, performance.id, payload)
        showToast('Performance atualizada com sucesso.', {
          type: 'success',
          theme: 'light',
        })
      } else {
        await createPolePerformance(eventId, poleId, payload)
        showToast('Performance criada com sucesso.', {
          type: 'success',
          theme: 'light',
        })
      }

      await Promise.resolve(onSaved())
      closeModal()
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, 'Nao foi possivel salvar a performance.'),
        {
          type: 'error',
          theme: 'light',
        },
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true
    const requestId = searchRequestIdRef.current + 1
    searchRequestIdRef.current = requestId

    const timer = window.setTimeout(async () => {
      try {
        if (normalizedArtistQuery.length === 1) {
          setArtists([])
          return
        }

        setIsLoadingArtists(true)
        const artistsResponse = await fetchArtistsOptions({
          query: normalizedArtistQuery || undefined,
          limit: 50,
        })

        if (!isMounted || searchRequestIdRef.current !== requestId) return
        setArtists(artistsResponse)
      } catch (error) {
        if (!isMounted || searchRequestIdRef.current !== requestId) return

        showToast(
          extractApiErrorMessage(error, 'Nao foi possivel carregar artistas.'),
          {
            type: 'error',
            theme: 'light',
          },
        )
      } finally {
        if (isMounted && searchRequestIdRef.current === requestId) {
          setIsLoadingArtists(false)
        }
      }
    }, 250)

    return () => {
      isMounted = false
      window.clearTimeout(timer)
    }
  }, [normalizedArtistQuery, showToast])

  useEffect(() => {
    if (!performance) return

    const artistId = performance.artistId ?? performance.artist?.id ?? ''
    const artistTitle = performance.artist?.title ?? ''

    setValue('artistId', artistId)

    if (artistId && artistTitle) {
      const artistFromPerformance = { id: artistId, title: artistTitle }
      setSelectedArtist(artistFromPerformance)
      setArtistQuery(artistTitle)
      setArtists((current) => {
        if (current.some((artist) => artist.id === artistId)) return current
        return [artistFromPerformance, ...current]
      })
    }

    setValue('title', performance.title ?? '')
    setValue('performanceDate', toDateTimeLocal(performance.performanceDate))
    setValue('startTime', performance.startTime ?? '')
    setValue('endTime', performance.endTime ?? '')
    setValue(
      'sortOrder',
      performance.sortOrder !== null ? `${performance.sortOrder ?? ''}` : '',
    )
    setValue('isHeadliner', Boolean(performance.isHeadliner))
    setValue('note', performance.note ?? '')
  }, [performance, setValue])

  const handleSelectArtist = (artist: ArtistOptionProps) => {
    setSelectedArtist(artist)
    setArtistQuery(artist.title)
    setValue('artistId', artist.id, { shouldValidate: true })
    setIsArtistDropdownOpen(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="relative">
        <h4 className="mb-2 text-sm font-bold">Artista</h4>
        <input type="hidden" {...register('artistId', { required: true })} />
        <input
          value={artistQuery}
          onChange={(event) => {
            const nextValue = event.currentTarget.value
            setArtistQuery(nextValue)

            if (
              selectedArtist &&
              nextValue.trim().toLowerCase() !==
                selectedArtist.title.trim().toLowerCase()
            ) {
              setSelectedArtist(null)
              setValue('artistId', '', { shouldValidate: true })
            }

            setIsArtistDropdownOpen(true)
          }}
          onFocus={() => setIsArtistDropdownOpen(true)}
          onBlur={() => {
            window.setTimeout(() => {
              setIsArtistDropdownOpen(false)

              if (!selectedArtist && selectedArtistId) {
                const selectedFromOptions = artists.find(
                  (artist) => artist.id === selectedArtistId,
                )

                if (selectedFromOptions) {
                  setSelectedArtist(selectedFromOptions)
                  setArtistQuery(selectedFromOptions.title)
                }
              }
            }, 120)
          }}
          placeholder="Digite para buscar artista"
          autoComplete="off"
          disabled={isLoadingArtists}
          className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none disabled:bg-gray-100"
        />

        {isArtistDropdownOpen && (
          <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
            {isLoadingArtists && (
              <p className="px-3 py-2 text-sm text-gray-500">Carregando...</p>
            )}

            {!isLoadingArtists && normalizedArtistQuery.length === 1 && (
              <p className="px-3 py-2 text-sm text-gray-500">
                Digite pelo menos 2 letras para buscar.
              </p>
            )}

            {!isLoadingArtists &&
              normalizedArtistQuery.length !== 1 &&
              artistOptions.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-500">
                  Nenhum artista encontrado.
                </p>
              )}

            {!isLoadingArtists &&
              artistOptions.map((artist) => (
                <button
                  key={artist.id}
                  type="button"
                  className={`block w-full px-3 py-2 text-left text-sm hover:bg-purple-50 ${
                    artist.id === selectedArtistId ? 'bg-purple-50' : ''
                  }`}
                  onMouseDown={() => handleSelectArtist(artist)}
                >
                  {artist.title}
                </button>
              ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Titulo</h4>
          <input
            {...register('title')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="Show principal"
          />
        </div>

        <div>
          <h4 className="mb-2 text-sm font-bold">Data da performance</h4>
          <input
            {...register('performanceDate', { required: true })}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Hora inicio</h4>
          <input
            {...register('startTime')}
            type="time"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Hora fim</h4>
          <input
            {...register('endTime')}
            type="time"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Ordem</h4>
          <input
            {...register('sortOrder')}
            type="number"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-700">
        <input {...register('isHeadliner')} type="checkbox" />
        Headliner
      </label>

      <div>
        <h4 className="mb-2 text-sm font-bold">Observacao</h4>
        <textarea
          {...register('note')}
          rows={3}
          className="w-full resize-none rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <Button
        title={performance ? 'Atualizar performance' : 'Criar performance'}
        variant="purple"
        isLoading={isLoading}
      />
    </form>
  )
}
